import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import { Loader2 } from 'lucide-react';

const PatientDetailModal = ({ isOpen, onClose, patient, onEdit, onSchedule }) => {
  if (!isOpen || !patient) return null;

  const [loading, setLoading] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [totalSessions, setTotalSessions] = useState(0);

  // Helper function to format phone numbers
  const formatPhone = (phone) => {
    if (!phone) return 'N/A';
    return phone;
  };

  // Helper function to format currency
  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    })?.format(value);
  };

  // Helper function to format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date)?.toLocaleDateString('pt-BR');
  };

  // Helper function to get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      confirmed: { label: 'Confirmado', className: 'bg-success/10 text-success' },
      completed: { label: 'Concluído', className: 'bg-primary/10 text-primary' },
      cancelled: { label: 'Cancelado', className: 'bg-destructive/10 text-destructive' },
      pending: { label: 'Pendente', className: 'bg-warning/10 text-warning' }
    };
    const config = statusConfig?.[status] || statusConfig?.pending;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config?.className}`}>
        {config?.label}
      </span>
    );
  };

  // ESC key handler to close modal
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event?.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen, onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-card rounded-xl shadow-2xl border border-border w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-xl font-semibold text-primary">
                {patient?.full_name?.split(' ')?.map(n => n?.[0])?.join('')?.slice(0, 2)?.toUpperCase()}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">{patient?.full_name}</h2>
              {patient?.email && <p className="text-sm text-muted-foreground">{patient?.email}</p>}
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {/* Patient Information Table - 2 columns */}
          <div className="bg-muted/50 rounded-lg p-6 mb-6">
            <table className="w-full">
              <tbody>
                <tr className="border-b border-border">
                  <td className="py-3 pr-4 text-sm font-medium text-muted-foreground w-1/2">Nome</td>
                  <td className="py-3 pl-4 text-base font-semibold text-foreground">{patient?.full_name}</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="py-3 pr-4 text-sm font-medium text-muted-foreground w-1/2">WhatsApp</td>
                  <td className="py-3 pl-4 text-base font-semibold text-foreground">
                    <div className="flex items-center gap-2">
                      {formatPhone(patient?.whatsapp_number || patient?.phone)}
                      <Icon name="MessageCircle" size={16} className="text-success" />
                    </div>
                  </td>
                </tr>
                <tr className="border-b border-border">
                  <td className="py-3 pr-4 text-sm font-medium text-muted-foreground w-1/2">Total de Sessões</td>
                  <td className="py-3 pl-4 text-base font-semibold text-foreground">
                    {loading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      `${totalSessions} ${totalSessions === 1 ? 'sessão realizada' : 'sessões realizadas'}`
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="py-3 pr-4 text-sm font-medium text-muted-foreground w-1/2">Valor da Consulta</td>
                  <td className="py-3 pl-4 text-base font-semibold text-foreground">
                    {formatCurrency(patient?.consultation_price)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {patient?.notes && (
            <div className="bg-muted/50 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2 mb-2">
                <Icon name="FileText" size={18} className="text-primary" />
                <span className="text-sm font-medium text-foreground">Observações</span>
              </div>
              <p className="text-sm text-muted-foreground">{patient?.notes}</p>
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-foreground">Histórico de Consultas</h3>
              {!loading && <span className="text-sm text-muted-foreground">{appointments?.length} registros</span>}
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : appointments?.length === 0 ? (
              <div className="bg-muted/50 rounded-lg p-8 text-center">
                <Icon name="Calendar" size={48} className="text-muted-foreground mx-auto mb-3" />
                <p className="text-base text-muted-foreground">
                  Ainda não há histórico de sessões para este paciente.
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {appointments?.map((visit) => (
                  <div key={visit?.id} className="bg-card border border-border rounded-lg p-4 hover:shadow-soft transition-smooth">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <Icon name="Calendar" size={18} className="text-primary" />
                        <div>
                          <p className="font-medium text-foreground">{formatDate(visit?.appointment_date)}</p>
                          <p className="text-xs text-muted-foreground">
                            {visit?.start_time?.slice(0, 5)} - {visit?.end_time?.slice(0, 5)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        {getStatusBadge(visit?.status)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <Button
            variant="outline"
            onClick={() => {
              onClose();
              onEdit(patient);
            }}
            iconName="Edit"
            iconPosition="left"
          >
            Editar Paciente
          </Button>
          <Button
            variant="default"
            onClick={() => {
              onClose();
              onSchedule(patient);
            }}
            iconName="Calendar"
            iconPosition="left"
          >
            Agendar Consulta
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PatientDetailModal;